// @flow

import * as React from 'react';
import classNames from '@ta/common.classnames';
import styles from './ManagedTextInput.css';

type EventCallback = (
  e: SyntheticKeyboardEvent<HTMLInputElement>,
  newValue: string,
  isComposing: boolean,
) => void;

/** Component props for a <ManagedTextInput> */
export type Props = {|
  /** input id */
  id?: string,

  /** The input form field name */
  name?: string,

  /** The initial value of the text input */
  value?: string,

  /** The input type to use */
  type?: string,

  /** A CSS class name to apply to the <input/> */
  className?: string,

  /** Placeholder text to appear in the input when there is no value */
  placeholder?: string,

  /** The IETF language tag of the input */
  lang?: string,

  /** The text direction of the input */
  dir?: 'ltr' | 'rtl' | 'auto',

  /** The minimum value to accept for this number or date input */
  min?: number | string,

  /** The maximum value to accept for this number or date input */
  max?: number | string,

  /** If the input should automatically focus after initial render */
  focusOnMount?: boolean,

  /** If the input should automatically focus after update */
  focusOnUpdate?: boolean,

  /** If the input should automatically hide browser's autocomplete after focus */
  hideAutoComplete?: boolean,

  /** If the input should be disabled */
  disabled?: boolean,

  /** Callback function for when the input gains focus */
  onFocus?: EventCallback,

  /** Callback function for when the input loses focus */
  onBlur?: EventCallback,

  /** Callback function for when the value changes */
  onChange?: EventCallback,

  /** Callback function for when a keyboard key is released */
  onKeyUp?: EventCallback,

  /** Callback function for when a keyboard key is pressed */
  onKeyDown?: EventCallback,

  /**
   * Callback function for when a composed character is started.
   */
  onCompositionStart?: EventCallback,

  /**
   * Callback function for when a composed character is finished. Note that when a composed character is completed that
   * onChange is not fired but this callback is.
   */
  onCompositionEnd?: EventCallback,

  /**
   * Callback function for when the user first interacts with the component (i.e. focus or change). Is only called
   * once.
   */
  onInteractionStart?: (e: SyntheticEvent<HTMLInputElement>) => void,

  /**
   * Callback function for when the user selects the element, or text within the element
   */
  onSelect?: EventCallback,

  /**
   * Callback to grab a ref to the text input
   */
  onRef?: (textInput: ?HTMLInputElement) => mixed,

  /**
   * The font size of the text input (ex. '18px', '1em', etc.)
   */
  fontSize?: string,

  /**
   * The line height of the text input (ex. '18px', '1em', etc.)
   */
  lineHeight?: string,

  /** The minimum length for this text input */
  minLength?: number,

  /** The maximum length for this text input */
  maxLength?: number,

  /** The click handler for external button */
  onCTAClick?: () => void,
|};

/** State for a <ManagedTextInput> */
export type State = {|
  /** True if the user has started interacting with the input, false else */
  interactionStarted: boolean,
  /** True if the user has started a composed character, false else */
  composing: boolean,
|};

/**
 * A managed text input field with some modified event callback signatures for convenience as well as a few convenience
 * event handles such as 'onInteractionStart'
 *
 * Note that this is a "Controlled Component" (see https://reactjs.org/docs/forms.html) and directly manages the value
 * of the input field in the component state. It is also set up such that an ancestor component may assume control
 * of this input component through the "onRef()" callback to get a ref to the HTMLInputElement as well as by passing
 * the "current" value through as a component prop which will in turn take prescedence overy any state maintained by
 * this component. This allows an ancestor component to do things like setting focus as well as change the current
 * value.
 */
class ManagedTextInput extends React.Component<Props, State> {
  static defaultProps = {
    type: 'text',
    focusOnMount: false,
    focusOnUpdate: false,
    hideAutoComplete: false,
    disabled: false,
  };

  componentDidMount() {
    if (this.props.focusOnMount && this.inputElem) {
      this.inputElem.focus();
    }
  }

  componentDidUpdate() {
    if (this.props.focusOnUpdate && this.inputElem) {
      this.inputElem.focus();
    }
  }

  // Need to disable this rule to programatically create the event listener functions
  // eslint-disable-next-line react/sort-comp
  newListener = (eventName: string, composing: boolean = false) => (
    e: SyntheticEvent<HTMLInputElement>,
  ) => {
    if (this.props.onInteractionStart && !this.interactionStarted) {
      this.props.onInteractionStart(e);
    }

    const { value } = e.currentTarget;
    if (this.props[eventName]) {
      this.props[eventName](e, value, this.composing);
    }
    // This is wrong, but this was the original behavior
    this.composing = composing;
    this.interactionStarted = true;
  };

  onFocus = this.newListener('onFocus');
  onBlur = this.newListener('onBlur');
  onChange = this.newListener('onChange');
  onKeyUp = this.newListener('onKeyUp');
  onKeyDown = this.newListener('onKeyDown');
  onCompositionStart = this.newListener('onCompositionStart', true);
  onCompositionEnd = this.newListener('onCompositionEnd');
  onSelect = this.newListener('onSelect');

  inputElem: ?HTMLInputElement;
  interactionStarted: boolean = false;
  composing: boolean = false;

  render() {
    const style = {
      fontSize: this.props.fontSize,
      lineHeight: this.props.lineHeight,
    };

    return (
      <input
        id={this.props.id}
        type={this.props.type}
        name={this.props.name}
        value={this.props.value}
        minLength={this.props.minLength}
        maxLength={this.props.maxLength}
        className={classNames(styles.managedInput, this.props.className)}
        style={style}
        placeholder={this.props.placeholder}
        lang={this.props.lang}
        dir={this.props.dir}
        min={this.props.min}
        max={this.props.max}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onChange={this.onChange}
        onKeyUp={this.onKeyUp}
        onSelect={this.onSelect}
        onKeyDown={this.onKeyDown}
        onCompositionStart={this.onCompositionStart}
        onCompositionEnd={this.onCompositionEnd}
        ref={input => {
          this.inputElem = input;
          if (this.props.onRef) {
            this.props.onRef(input);
          }
        }}
        autoComplete={this.props.hideAutoComplete ? 'off' : 'on'}
        disabled={this.props.disabled}
      />
    );
  }
}

export default ManagedTextInput;
